import type { MembershipCategory } from '@/utils/membership/types';
import { RequirementType } from '@/utils/membership/types';

import { type ApplicationCreateDTO, type AwardCreateDTO, type EducationCreateDTO, type EmploymentHistoryCreateDTO, type ProfessionalAffiliationCreateDTO, type SupportingDocumentCreateDTO } from '@/utils/api/membership-service/models';
import type {
  AffiliationDraftEntry,
  AwardDraftEntry,
  EducationHistoryDraftEntry,
  EmploymentHistoryDraftEntry,
  MembershipApplicationDraft,
  MembershipApplicationRequirementsDraft,
  SupportingDocumentDraft,
} from '../api/useMembershipApplicationDraft';
import {
  getCategoryDocumentRequirements,
  getCategoryNonDocumentRequirements,
} from '../data/category-requirements';

interface BuildApplicationPayloadArgs {
  draft: MembershipApplicationDraft
  category?: MembershipCategory | null
  categoryName?: string | null
}

interface RequirementAnswer {
  id?: number
  label: string
  type: RequirementType
  mandatory: boolean
  value: string | number | boolean
}

interface RequirementFileMeta {
  id?: number
  label: string
  fileId?: string
  fileUrl?: string
  originalFilename?: string
  mandatory: boolean
}

interface DocumentMeta {
  documentType: string
  fileId?: string
  fileUrl?: string
  originalFilename?: string
  mandatory?: boolean
  notes?: string
}

interface SupplementaryPayload {
  categoryMeta?: {
    categoryId?: number | null
    prerequisitesAccepted: boolean
  }
  requirements?: RequirementAnswer[]
  requirementFiles?: RequirementFileMeta[]
  documentsMeta?: DocumentMeta[]
  educationDetails?: Array<{
    id?: string
    awardingBody?: string
    honors?: string
  }>
  affiliationNotes?: Array<{
    id?: string
    organisation?: string
    notes?: string
  }>
}

function buildRequirementFieldKey(label: string, id?: number) {
  if (typeof id === 'number') {
    return `req_${id}`
  }
  const sanitised = label.toLowerCase().replace(/[^a-z0-9]+/g, '_')
  return `req_${sanitised}`
}

function parseRequirementFileValue(
  rawValue: unknown,
): { fileId?: string; fileUrl?: string; originalFilename?: string } | null {
  if (typeof rawValue !== 'string') return null
  const trimmed = rawValue.trim()
  if (!trimmed) return null
  try {
    const parsed = JSON.parse(trimmed) as {
      fileId?: string
      fileUrl?: string
      originalFilename?: string
    }
    if (parsed && typeof parsed === 'object') {
      return parsed
    }
  } catch (_error) {
    return null
  }
  return null
}

function mapEmploymentHistory(entries: EmploymentHistoryDraftEntry[]): EmploymentHistoryCreateDTO[] {
  const result: EmploymentHistoryCreateDTO[] = []

  for (const entry of entries) {
    const hasCoreDetails =
      entry.organisation?.trim().length || entry.jobTitle?.trim().length || entry.startDate?.trim().length
    if (!hasCoreDetails) {
      continue
    }

    const responsibilities: string[] = []
    if (entry.responsibilities?.trim()) {
      responsibilities.push(entry.responsibilities.trim())
    }
    if (entry.reportingTo?.trim()) {
      responsibilities.push(`Reports to: ${entry.reportingTo.trim()}`)
    }

    result.push({
      companyName: entry.organisation?.trim(),
      position: entry.jobTitle?.trim(),
      startDate: entry.startDate?.trim() || undefined,
      endDate: entry.currentlyEmployed ? undefined : entry.endDate?.trim() || undefined,
      responsibilities: responsibilities.length > 0 ? responsibilities.join('\n') : undefined,
      isCurrent: entry.currentlyEmployed || undefined,
    })
  }

  return result
}

function normaliseGraduationYear(year?: string) {
  if (!year) return undefined
  const trimmed = year.trim()
  if (!/^\d{4}$/.test(trimmed)) {
    return undefined
  }
  return `${trimmed}-12-31`
}

function mapEducation(entries: EducationHistoryDraftEntry[], supplementary: SupplementaryPayload): EducationCreateDTO[] {
  const result: EducationCreateDTO[] = []

  for (const entry of entries) {
    const hasCoreDetails =
      entry.institution?.trim().length || entry.qualification?.trim().length || entry.awardingBody?.trim().length
    if (!hasCoreDetails) {
      continue
    }

    if (entry.awardingBody?.trim() || entry.honors?.trim()) {
      if (!supplementary.educationDetails) {
        supplementary.educationDetails = []
      }
      supplementary.educationDetails.push({
        id: entry.id,
        awardingBody: entry.awardingBody?.trim() || undefined,
        honors: entry.honors?.trim() || undefined,
      })
    }

    result.push({
      institutionName: entry.institution?.trim(),
      qualification: entry.qualification?.trim(),
      fieldOfStudy: entry.fieldOfStudy?.trim() || undefined,
      endDate: normaliseGraduationYear(entry.graduationYear),
    })
  }

  return result
}

function mapAffiliations(entries: AffiliationDraftEntry[], supplementary: SupplementaryPayload): ProfessionalAffiliationCreateDTO[] {
  const result: ProfessionalAffiliationCreateDTO[] = []

  for (const entry of entries) {
    const hasCoreDetails = entry.organisation?.trim().length
    if (!hasCoreDetails) {
      continue
    }

    if (entry.notes?.trim()) {
      if (!supplementary.affiliationNotes) {
        supplementary.affiliationNotes = []
      }
      supplementary.affiliationNotes.push({
        id: entry.id,
        organisation: entry.organisation?.trim(),
        notes: entry.notes.trim(),
      })
    }

    result.push({
      organizationName: entry.organisation?.trim(),
      membershipNumber: entry.membershipNumber?.trim() || undefined,
      membershipLevel: entry.role?.trim() || undefined,
      joinDate: entry.since?.trim() || undefined,
    })
  }

  return result
}

function mapAwards(entries: AwardDraftEntry[]): AwardCreateDTO[] {
  const result: AwardCreateDTO[] = []

  for (const entry of entries) {
    const hasCoreDetails = entry.title?.trim().length || entry.issuer?.trim().length
    if (!hasCoreDetails) {
      continue
    }

    result.push({
      awardName: entry.title?.trim(),
      issuingOrganisation: entry.issuer?.trim() || undefined,
      dateReceived: entry.awardedOn?.trim(),
      description: entry.description?.trim(),
    })
  }

  return result
}

function mapSupportingDocuments(
  documents: SupportingDocumentDraft[],
  supplementary: SupplementaryPayload,
): SupportingDocumentCreateDTO[] {
  const result: SupportingDocumentCreateDTO[] = []

  for (const doc of documents) {
    if (doc.status !== 'uploaded') {
      continue
    }

    if (!doc.fileId || doc.fileId.trim().length === 0) {
      const label = doc.documentType || doc.originalFilename || doc.fileName || doc.id
      throw new Error(`Supporting document "${label ?? 'unknown'}" is missing a file reference.`)
    }

    if (!supplementary.documentsMeta) {
      supplementary.documentsMeta = []
    }
    supplementary.documentsMeta.push({
      documentType: doc.documentType,
      fileId: doc.fileId,
      fileUrl: doc.fileUrl,
      originalFilename: doc.originalFilename ?? doc.fileName,
      mandatory: doc.mandatory,
      notes: doc.notes,
    })

    const supportingDocument: SupportingDocumentCreateDTO = {
      documentType: doc.documentType,
      fileId: doc.fileId,
      fileName: doc.originalFilename ?? doc.fileName ?? '',
      fileUrl: doc.fileUrl || '',
      description: doc.notes ?? undefined,
    }

    if (supportingDocument.documentType || supportingDocument.fileName || supportingDocument.fileUrl) {
      result.push(supportingDocument)
    }
  }

  return result
}

function collectRequirementData(
  requirementsDraft: MembershipApplicationRequirementsDraft | undefined,
  category: MembershipCategory | null | undefined,
  supplementary: SupplementaryPayload,
) {
  const requirementAnswers: RequirementAnswer[] = []
  const requirementFiles: RequirementFileMeta[] = []

  if (!requirementsDraft) {
    return { requirementAnswers, requirementFiles }
  }

  const categoryReference = category ?? undefined
  const summaries = [
    ...getCategoryNonDocumentRequirements(categoryReference),
    ...getCategoryDocumentRequirements(categoryReference),
  ]
  const usedKeys = new Set<string>()

  for (const summary of summaries) {
    const key = buildRequirementFieldKey(summary.label, summary.id)
    usedKeys.add(key)
    const rawValue = requirementsDraft[key]
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      continue
    }

    if (summary.type === RequirementType.FILE) {
      const parsed = parseRequirementFileValue(rawValue)
      if (parsed && parsed.fileId) {
        requirementFiles.push({
          id: summary.id,
          label: summary.label,
          fileId: parsed.fileId,
          fileUrl: parsed.fileUrl,
          originalFilename: parsed.originalFilename,
          mandatory: summary.mandatory,
        })
      } else if (parsed && !parsed.fileId) {
        throw new Error(`Requirement "${summary.label}" is missing a file reference.`)
      }
      continue
    }

    if (
      typeof rawValue === 'string' ||
      typeof rawValue === 'number' ||
      typeof rawValue === 'boolean'
    ) {
      requirementAnswers.push({
        id: summary.id,
        label: summary.label,
        type: summary.type,
        mandatory: summary.mandatory,
        value: typeof rawValue === 'string' ? rawValue.trim() : rawValue,
      })
    }
  }

  // Capture any remaining values not matched to current requirement definitions
  for (const [rawKey, rawValue] of Object.entries(requirementsDraft)) {
    if (usedKeys.has(rawKey)) {
      continue
    }
    if (
      typeof rawValue === 'string' ||
      typeof rawValue === 'number' ||
      typeof rawValue === 'boolean'
    ) {
      requirementAnswers.push({
        label: rawKey,
        type: RequirementType.TEXT,
        mandatory: false,
        value: typeof rawValue === 'string' ? rawValue.trim() : rawValue,
      })
    } else {
      const parsed = parseRequirementFileValue(rawValue)
      if (parsed && parsed.fileId) {
        requirementFiles.push({
          label: rawKey,
          fileId: parsed.fileId,
          fileUrl: parsed.fileUrl,
          originalFilename: parsed.originalFilename,
          mandatory: false,
        })
      } else if (parsed && !parsed.fileId) {
        throw new Error(`Requirement "${rawKey}" is missing a file reference.`)
      }
    }
  }

  if (requirementAnswers.length > 0) {
    supplementary.requirements = requirementAnswers
  }
  if (requirementFiles.length > 0) {
    supplementary.requirementFiles = requirementFiles
  }

  return { requirementAnswers, requirementFiles }
}

export function buildApplicationPayload({
  draft,
  category,
  categoryName,
}: BuildApplicationPayloadArgs): ApplicationCreateDTO {
  if (!draft.profile) {
    throw new Error('Profile information is incomplete')
  }

  if (!draft.category || !draft.category.categoryId) {
    throw new Error('Membership category selection is missing')
  }

  const supplementary: SupplementaryPayload = {}

  supplementary.categoryMeta = {
    categoryId: draft.category.categoryId,
    prerequisitesAccepted: Boolean(draft.category.prerequisitesAccepted),
  }

  const { requirementFiles } = collectRequirementData(draft.requirements, category ?? undefined, supplementary)

  const documentsFromDraft = mapSupportingDocuments(draft.supportingDocuments ?? [], supplementary)
  const documentsFromRequirements: SupportingDocumentCreateDTO[] = requirementFiles.map((file) => {
    if (!file.fileId || file.fileId.trim().length === 0) {
      const label = file.label ?? (typeof file.id === 'number' ? `#${file.id}` : 'Supporting document')
      throw new Error(`Requirement document "${label}" is missing a file reference.`)
    }

    if (!supplementary.documentsMeta) {
      supplementary.documentsMeta = []
    }
    supplementary.documentsMeta.push({
      documentType: file.label,
      fileId: file.fileId,
      fileUrl: file.fileUrl,
      originalFilename: file.originalFilename,
      mandatory: file.mandatory,
    })

    return {
      documentType: file.label,
      fileName: file.originalFilename ?? '',
      fileUrl: file.fileUrl ?? '',
      fileId: file.fileId ?? '',
    }
  })

  const documents = [...documentsFromDraft, ...documentsFromRequirements]

  const profile = draft.profile
  const application: ApplicationCreateDTO = {
    categoryName: categoryName ?? category?.categoryName ?? '',
    firstName: profile.firstName?.trim(),
    middleName: profile.middleName?.trim() || undefined,
    lastName: profile.lastName?.trim(),
    emailAddress: profile.email?.trim(),
    phoneNumbers: profile.phoneNumber?.trim() || undefined,
    organisation: profile.organisation?.trim() || undefined,
    profileLinkUrl: profile.profileLinkUrl?.trim() || undefined,
    dateOfBirth: profile.dateOfBirth || undefined,
    gender: profile.gender,
    employmentHistory: mapEmploymentHistory(draft.employmentHistory ?? []),
    education: mapEducation(draft.educationHistory ?? [], supplementary),
    affiliations: mapAffiliations(draft.professionalAffiliations ?? [], supplementary),
    awards: mapAwards(draft.professionalAwards ?? []),
    documents: documents.length > 0 ? documents : undefined,
  }

  // Strip empty arrays to avoid sending empty payloads
  if (application.employmentHistory && application.employmentHistory.length === 0) {
    delete application.employmentHistory
  }
  if (application.education && application.education.length === 0) {
    delete application.education
  }
  if (application.affiliations && application.affiliations.length === 0) {
    delete application.affiliations
  }
  if (application.awards && application.awards.length === 0) {
    delete application.awards
  }
  if (application.documents && application.documents.length === 0) {
    delete application.documents
  }

  return application
}
